// Imports => React
import { useMemo } from 'react';
import { withStore } from '@stores';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';

// Imports => Config
import config from '@config';

// Imports => Constants
import { ICONS, KEYS, THEMES, TYPES } from '@opp/ui-kit';

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcRichContent,
  AcCard,
  AcButton,
  AcIcon,
} from '@opp/ui-kit';

// Imports => Molecules
import AcDeleteAccountModal from '@molecules/ac-delete-account-modal/ac-delete-account-modal';
import {withTranslation} from "react-i18next";

const _CLASSES = {
  MAIN: 'ac-profile-security-tab ac-profile__grid-container',
};

const AcProfileSecurityTab = ({ data, store: { profile, ui }, t }) => {
  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const handleDeleteMerchantAccount = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (event && event.stopPropagation) event.stopPropagation();

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t('TITLES.DELETE_ACCOUNT', {ns: 'titles'})}`,
      name: 'delete-account-modal',
      body: <AcDeleteAccountModal/>,
      closeable: true,
      visible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  }

  return (
    <div className={getMainClassNames}>
      <AcCard className={'ac-profile__column ac-profile__column--half'}>
        <AcContainer>
          <AcRow>
            <AcColumn>
              <AcHeading theme={THEMES.DARK} rank={6}>
                {t('security.password', 'Password', {ns: 'account'})}
              </AcHeading>
            </AcColumn>
          </AcRow>

          <AcRow className={'h-margin-bottom-15'}>
            <AcColumn>
              <AcRichContent content={`<p>${t('security.password-sub', 'Set or update your password.', {ns: 'account'})}</p>`} />
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn>
              <AcButton
                theme={THEMES.OMEGA}
                tag={'a'}
                target={'_blank'}
                href={`${config.pkce.accounts_uri}/dashboard/account-and-security/password`}
              >
                <span>{t('security.change-password', 'Change your password', {ns: 'account'})}</span>
                <AcIcon icon={ICONS.OPEN_IN_NEW} />
              </AcButton>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </AcCard>

      <AcCard
        className={
          'ac-profile__column ac-profile__column--half h-hidden-xxs h-hidden-xs h-hidden-sm'
        }
        theme={THEMES.TRANSPARENT}
      ></AcCard>

      <AcCard className={'ac-profile__column ac-profile__column--half'}>
        <AcContainer>
          <AcRow>
            <AcColumn>
              <AcHeading theme={THEMES.DARK} rank={6}>
                {t('security.2fa', 'Two-factor authentication', {ns: 'account'})}
              </AcHeading>
            </AcColumn>
          </AcRow>

          <AcRow className={'h-margin-bottom-15'}>
            <AcColumn>
              <AcRichContent
                content={
                  `<p>${t('security.2fa-sub', 'Add an extra layer of security to your account by using a one-time security code in addition to your password each time you log in.', {ns: 'account'})}</p>`
                }
              />
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn>
              <AcButton
                theme={THEMES.OMEGA}
                tag={'a'}
                target={'_blank'}
                href={`${config.pkce.accounts_uri}/dashboard/account-and-security/two-factor/setup/select-method`}
              >
                <span>{t('security.setup-2fa', 'Set up two-factor authentication', {ns: 'account'})}</span>
                <AcIcon icon={ICONS.OPEN_IN_NEW} />
              </AcButton>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </AcCard>

      <AcCard
        className={
          'ac-profile__column ac-profile__column--half h-hidden-xxs h-hidden-xs h-hidden-sm'
        }
        theme={THEMES.TRANSPARENT}
      ></AcCard>

      <AcCard className={'ac-profile__column ac-profile__column--half'}>
        <AcContainer>
          <AcRow>
            <AcColumn>
              <AcHeading theme={THEMES.DARK} rank={6}>
                {t('security.delete-account', 'Delete merchant account', {ns: 'account'})}
              </AcHeading>
            </AcColumn>
          </AcRow>

          <AcRow className={'h-margin-bottom-15'}>
            <AcColumn>
              <AcRichContent
                content={
                  `<p>${t('security.delete-account-sub', 'If there are no transactions processed your account will be removed immediately. If there are processed transactions we are obliged by law to keep your data for 7 years. After this period your data will be deleted.', {ns: 'account'})}</p>`
                }
              />
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn>
              <AcButton
                theme={THEMES.ERROR}
                type={TYPES.BUTTON}
                title={`${t('security.delete-account', 'Delete merchant account', {ns: 'account'})}`}
                callback={handleDeleteMerchantAccount}
              >
                <span>{t('security.delete-account', 'Delete merchant account', {ns: 'account'})}</span>
                <AcIcon icon={ICONS.DELETE_EMPTY_OUTLINE} />
              </AcButton>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </AcCard>
    </div>
  );
};

export default withTranslation(['account', 'titles'])(withStore(observer(AcProfileSecurityTab)));
